import { Card, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import Button from "../Button";
import { FlatCard } from "../Cards/FlatCard";
import { CmeCertificateIllustration } from "./CmeCertificateIllustration";
import { CmeCreditTable } from "./CmeCreditTable";
import { CmeDownloadNotice } from "./CmeDownloadNotice";
import type {
	CmeAction,
	CmeCreditRowData,
	CmeDownloadNoticeData,
} from "./types";

export type CmeCertificateCardProps = {
	completionLabel: string;
	title: string;
	subhead: string;
	creditTableCaption: string;
	credits: CmeCreditRowData[];
	primaryAction: CmeAction;
	secondaryAction?: CmeAction;
	footnote: string;
	downloadNotice?: CmeDownloadNoticeData;
};

export const CmeCertificateCard = ({
	completionLabel,
	title,
	subhead,
	creditTableCaption,
	credits,
	primaryAction,
	secondaryAction,
	footnote,
	downloadNotice,
}: CmeCertificateCardProps) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	const content = (
		<Box gap={24} alignItems="stretch">
			<Box gap={16}>
				<Box gap={4} alignItems="center">
					<CmeCertificateIllustration size={78} />
					<Box
						paddingHorizontal={8}
						paddingVertical={6}
						borderRadius={theme.roundness * 100}
						backgroundColor={
							theme.dark
								? theme.palette.success.dark
								: theme.palette.success.info
						}
					>
						<Text
							variant="labelMedium"
							style={{
								color: theme.dark
									? theme.palette.success.info
									: theme.palette.success.dark,
							}}
						>
							{completionLabel}
						</Text>
					</Box>
					<Text variant="titleLarge" style={{ textAlign: "center" }}>
						{title}
					</Text>
					<Text variant="bodyMedium" style={{ textAlign: "center" }}>
						{subhead}
					</Text>
				</Box>
				<CmeCreditTable caption={creditTableCaption} credits={credits} />
				{downloadNotice ? <CmeDownloadNotice notice={downloadNotice} /> : null}
			</Box>
			{!isMobile && (
				<Box flexDirection="row" gap={16} justifyContent="center">
					<Button
						mode="contained"
						icon={primaryAction.iconName}
						onPress={primaryAction.onPress}
					>
						{primaryAction.label}
					</Button>
					{secondaryAction ? (
						<Button
							mode="outlined"
							icon={secondaryAction.iconName}
							onPress={secondaryAction.onPress}
						>
							{secondaryAction.label}
						</Button>
					) : null}
				</Box>
			)}
			<Text
				variant="bodySmall"
				style={{
					color: theme.colors.onSurfaceVariant,
					textAlign: "center",
				}}
			>
				{footnote}
			</Text>
		</Box>
	);

	if (isMobile) {
		return content;
	}

	return (
		<FlatCard style={{ borderRadius: theme.roundness * 3 }}>
			<Card.Content style={{ padding: 24 }}>{content}</Card.Content>
		</FlatCard>
	);
};
